// Enhanced notification service with context-aware reminders
import { supabase } from '@/integrations/supabase/client';
import { openAIService } from './openaiService';

export interface UserContext {
  idea?: string;
  developmentStage?: string;
  recentTasks?: string[];
  roiData?: any;
  socialStreak?: number;
  lastSocialActivity?: string;
}

export class NotificationService {
  private static lastReminderTime: number = 0;
  private static reminderInterval: number = 12 * 60 * 1000; // 12 minutes for variety

  static async createContextualNotification(userId: string, context: UserContext): Promise<string> {
    try {
      const prompt = `As Cashpurr, generate a brief, witty notification for a founder based on their context:
      - Idea: ${context.idea || 'Not specified'}
      - Stage: ${context.developmentStage || 'Unknown'}
      - Recent tasks: ${context.recentTasks?.join(', ') || 'None'}
      - Social streak: ${context.socialStreak || 0} days

      Generate a specific, actionable reminder that's encouraging but direct. Keep it under 100 characters and include relevant emoji.`;

      const notification = await openAIService.createChatCompletion([
        { role: 'user', content: prompt }
      ], {
        temperature: 0.7,
        max_tokens: 150
      });

      return notification;
    } catch (error) {
      console.error('Error generating contextual notification:', error);
      return this.getDefaultReminder();
    }
  }

  static getDefaultReminder(): string {
    const defaults = [
      "Time to sprint! Check your dashboard for today's tasks! 🏃‍♂️",
      "Meow! Don't forget to update your ROI tracker today! 📊",
      "Your idea won't validate itself—take action! 💡",
      "Social media silence = missed opportunities! Post something! 📱",
      "Journal your progress—reflection breeds success! 📝"
    ];
    return defaults[Math.floor(Math.random() * defaults.length)];
  }

  static async checkComprehensiveReminders(userId: string): Promise<void> {
    const now = Date.now();
    if (now - this.lastReminderTime < this.reminderInterval) {
      return; // Don't spam notifications
    }
    
    this.lastReminderTime = now;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Check multiple app activities
      const [socialLogs, tasks, roiEntries, journalEntries, profile] = await Promise.all([
        supabase
          .from('social_logs')
          .select('date')
          .eq('user_id', userId)
          .eq('date', today)
          .limit(1),
        supabase
          .from('tasks')
          .select('id, title, completed, due_date')
          .eq('user_id', userId)
          .eq('completed', false)
          .order('due_date', { ascending: true })
          .limit(5),
        supabase
          .from('roi_entries')
          .select('date')
          .eq('user_id', userId)
          .eq('date', today)
          .limit(1),
        supabase
          .from('journal_entries')
          .select('date')
          .eq('user_id', userId)
          .eq('date', today)
          .limit(1),
        supabase
          .from('profiles')
          .select('idea_description, development_stage')
          .eq('user_id', userId)
          .single()
      ]);

      const reminders = [];
      
      // More diverse task reminders
      if (tasks.data && tasks.data.length > 0) {
        const overdueTasks = tasks.data.filter(task => 
          task.due_date && new Date(task.due_date) < new Date()
        );
        if (overdueTasks.length > 0) {
          const overdueReminders = [
            `OVERDUE: "${overdueTasks[0].title}"—investors hate delays! ⚠️`,
            `Your task "${overdueTasks[0].title}" is overdue—time to sprint! 🏃‍♂️`,
            `Meow! "${overdueTasks[0].title}" is past due—get it DONE! 😿⏰`
          ];
          reminders.push(overdueReminders[Math.floor(Math.random() * overdueReminders.length)]);
        } else {
          const taskReminders = [
            `Pending: "${tasks.data[0].title}"—make it happen today! 🎯`,
            `Your next task: "${tasks.data[0].title}"—let's crush it! 💪`,
            `Task alert: "${tasks.data[0].title}" needs your attention! 📋`
          ];
          reminders.push(taskReminders[Math.floor(Math.random() * taskReminders.length)]);
        }
      }

      // Diverse social media reminders
      if (!socialLogs.data || socialLogs.data.length === 0) {
        const socialReminders = [
          "Zero social posts today—your competitors are posting! 📱💼",
          "Silent on socials = invisible to investors! Post now! 📢",
          "LinkedIn is your stage—where's today's performance? 🎭",
          "Social media builds your brand—log some activity! 🚀",
          "Your audience is waiting—share your journey today! 📝",
          "No social activity = missed opportunities! Fix it! ⚡"
        ];
        reminders.push(socialReminders[Math.floor(Math.random() * socialReminders.length)]);
      }

      // ROI tracking variety
      if (!roiEntries.data || roiEntries.data.length === 0) {
        const roiReminders = [
          "Numbers don't lie—update your ROI tracker today! 📊",
          "Financial discipline wins—log your ROI metrics! 💰",
          "Revenue tracking = success tracking—update now! 📈",
          "Your ROI data is missing—investors notice this! 🔍"
        ];
        reminders.push(roiReminders[Math.floor(Math.random() * roiReminders.length)]);
      }

      // Journal entry variety
      if (!journalEntries.data || journalEntries.data.length === 0) {
        const journalReminders = [
          "Reflection breeds success—journal your progress! 📝",
          "Document today's wins and challenges! 🏆",
          "Your founder journey deserves documentation! 📖",
          "Journaling unlocks insights—write today's entry! 💡"
        ];
        reminders.push(journalReminders[Math.floor(Math.random() * journalReminders.length)]);
      }

      // Generate AI-powered contextual reminder if we have profile data
      if (reminders.length > 0 && profile.data) {
        try {
          const context: UserContext = {
            idea: profile.data.idea_description,
            developmentStage: profile.data.development_stage,
            recentTasks: tasks.data?.slice(0, 3).map(t => t.title) || []
          };
          
          const aiReminder = await this.createContextualNotification(userId, context);
          if (aiReminder && aiReminder !== this.getDefaultReminder()) {
            reminders.push(aiReminder);
          }
        } catch (error) {
          console.log('AI reminder generation failed, using default');
        }
      }

      // Display one random reminder if any exist
      if (reminders.length > 0) {
        const selectedReminder = reminders[Math.floor(Math.random() * reminders.length)];
        console.log('Contextual reminder:', selectedReminder);
        
        // Store notification in notifications table
        await supabase.from('notifications').insert({
          user_id: userId,
          content: selectedReminder,
          type: 'reminder',
          context: { source: 'activity_check', generated_at: new Date().toISOString() }
        });
      }
    } catch (error) {
      console.error('Error checking comprehensive reminders:', error);
    }
  }

  static stopRemindersForPlatform(platform: string) {
    console.log(`Stopping reminders for ${platform}`);
    // Implementation for stopping platform-specific reminders
  }

  static async generateCashpurrMotivation(context: UserContext): Promise<string> {
    const motivations = [
      "Keep sprinting! You're making great progress! 😸",
      "Meow! Time to turn those ideas into cold hard cash! 💰",
      "Your 30-day sprint is purr-fect for building momentum! 🚀",
      "Every task completed gets you closer to your cash goal! 💪",
      "Stop procrastinating and start cash-generating! 📈"
    ];
    
    return motivations[Math.floor(Math.random() * motivations.length)];
  }

  static async schedulePeriodicNotifications(userId: string): Promise<void> {
    console.log('Scheduling periodic notifications for user:', userId);
    
    // Check for comprehensive reminders
    await this.checkComprehensiveReminders(userId);
  }

  static async generateDailyNotification(userId: string): Promise<void> {
    console.log('Generating daily notification for user:', userId);
  }
}

export const notificationService = new NotificationService();